// src/components/ContactUs/ContactUs.js
import React, { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";

import {
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import { motion } from "framer-motion";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SendIcon from "@mui/icons-material/Send";
import styles from "./ContactUs.module.css";

const ContactUs = () => {
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      emailjs.init(publicKey);
    } else {
      console.error("EmailJS Public Key is missing from .env file");
    }
  }, []);

  const [formData, setFormData] = useState({
    from_name: "",
    from_email: "",
    phone: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });

    const templateParams = {
      ...formData,
      time: new Date().toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }),
    };

    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    emailjs
      .send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)
      .then(
        (response) => {
          console.log("SUCCESS!", response.status, response.text);

          setStatus({
            type: "success",
            message: "Thank you! Your message has been sent successfully.",
          });

          setFormData({
            from_name: "",
            from_email: "",
            phone: "",
            message: "",
          });

          setTimeout(() => {
            setStatus({ type: "", message: "" });
          }, 5000);
        },
        (error) => {
          console.error("FAILED...", error);
          setStatus({
            type: "error",
            message: `Submission failed: ${error?.text || "Check your Public Key and .env setup."}`,
          });
        },
      )
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className={styles.container}>
      {/* Background effects */}
      <div className={styles.binaryBackground}></div>
      <div className={styles.circuitLines}></div>

      <div className={styles.contentGrid}>
        {/* Left Column: Get In Touch */}
        <div className={styles.leftColumn}>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "3.5rem",
                fontFamily: "'Raleway', 'Poppins', sans-serif",
                mb: 2,
                background: "linear-gradient(90deg, #ffffff 0%, #00d4ff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                letterSpacing: "2px",
                textTransform: "uppercase",
              }}
            >
              Get In Touch
            </Typography>
            <Typography className={styles.sectionTitle}>
              
            </Typography>            

            <Typography className={styles.description}>
              Have questions? We'd love to hear from you. Send us a message and
              we'll respond as soon as possible.
            </Typography>

            <div className={styles.contactDetails}>
              <div className={styles.contactItem}>
                <PhoneIcon className={styles.contactIcon} />
                <div>
                  <Typography className={styles.contactLabel}>Phone</Typography>
                  <Typography className={styles.contactValue}>
                    +91 70304 03299
                  </Typography>
                </div>
              </div>

              <div className={styles.contactItem}>
                <EmailIcon className={styles.contactIcon} />
                <div>
                  <Typography className={styles.contactLabel}>Email</Typography>
                  <Typography className={styles.contactValue}>
                    contact@aimebot.com
                  </Typography>
                </div>
              </div>

              <div className={styles.contactItem}>
                <AccessTimeIcon className={styles.contactIcon} />
                <div>
                  <Typography className={styles.contactLabel}>
                    Working Hours
                  </Typography>
                  <Typography className={styles.contactValue}>
                    24/7 Support Available
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column: Send Us a Message Form */}
        <div className={styles.rightColumn}>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card className={styles.formCard}>
              <div className={styles.cardAccent}></div>

              <CardContent className={styles.cardContent}>
                <Typography className={styles.formTitle}
                  sx={{ fontFamily: "'Raleway', 'Poppins', sans-serif" }}>
                  Send Us a Message
                </Typography>
                <Typography className={styles.formSubtitle}
                sx={{ fontFamily: "'Raleway', 'Poppins', sans-serif" }}>
                  Fill out the form below and we'll get back to you as soon as
                  possible
                </Typography>

                <form onSubmit={handleSubmit} className={styles.contactForm}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="from_name"
                        value={formData.from_name}
                        onChange={handleChange}
                        className={styles.inputField}
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="from_email"
                        type="email"
                        value={formData.from_email}
                        onChange={handleChange}
                        className={styles.inputField}
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={styles.inputField}
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className={styles.inputField}
                        multiline
                        rows={5}
                        required
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>

                  {status.message && (
                    <Alert
                      severity={status.type}
                      sx={{
                        mt: 2,
                        borderRadius: "12px",
                        backgroundColor:
                          status.type === "success"
                            ? "rgba(76, 175, 80, 0.1)"
                            : "rgba(244, 67, 54, 0.1)",
                        border:
                          status.type === "success"
                            ? "1px solid rgba(76, 175, 80, 0.3)"
                            : "1px solid rgba(244, 67, 54, 0.3)",
                        color: "white",
                      }}
                    >
                      {status.message}
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    className={styles.submitButton}
                    sx={{ mt: 3 }}
                    endIcon={!loading && <SendIcon />}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
